import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AccountDetail } from '@/types/AccountDetail';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Area,
  AreaChart,
  BarChart,
  Bar,
} from 'recharts';
import { fetchAccountDetail } from '@/services/api/accountsApi';

const AccountDetailPage: React.FC = () => {
  const { accountId } = useParams<{ accountId: string }>();
  const [account, setAccount] = useState<AccountDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const data = await fetchAccountDetail(Number(accountId));
      setAccount(data);
      setLoading(false);
    };
    load();
  }, [accountId]);

  if (loading || !account) return <div className="p-4">Loading...</div>;

  // ✅ Optionally sort balance history by date (if not sorted)
  const sortedHistory = [...account.balanceHistory].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  return (
    <div className="space-y-6">
      <Link
        to="/accounts"
        className="text-emerald-600 hover:text-emerald-800 text-sm font-medium underline mb-2 inline-block"
      >
        ← Accounts
      </Link>

      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
        {account.name} – ${account.currentBalance.toFixed(2)}
      </h1>

      {/* 📊 Interactive Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Balance History</CardTitle>
        </CardHeader>
        <CardContent className="h-80">
          <ResponsiveContainer width="100%" height="100%">
  <BarChart data={sortedHistory}>
    <CartesianGrid strokeDasharray="3 3" />
    <XAxis
      dataKey="date"
      tick={{ fontSize: 12 }}
      tickFormatter={(date) => new Date(date).toLocaleDateString()}
    />
    <YAxis
      tickFormatter={(val) => `$${val}`}
      tick={{ fontSize: 12 }}
    />
    <Tooltip
      formatter={(value: any) => [`$${value.toFixed(2)}`, 'Balance']}
      labelFormatter={(label: any) => new Date(label).toLocaleDateString()}
    />
    <Bar
      dataKey="balance"
      fill="#10B981"
      radius={[4, 4, 0, 0]}
      barSize={40}
    />
  </BarChart>
</ResponsiveContainer>

        </CardContent>
      </Card>

      {/* 💳 Transactions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Transactions</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
              <tr>
                <th className="px-4 py-2 text-left">Date</th>
                <th className="px-4 py-2 text-left">Name</th>
                <th className="px-4 py-2 text-left">Category</th>
                <th className="px-4 py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {account.transactions.map(txn => (
                <tr key={txn.id} className="border-t dark:border-gray-700">
                  <td className="px-4 py-2">
                    {new Date(txn.date).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2">{txn.name}</td>
                  <td className="px-4 py-2">{txn.category}</td>
                  <td
                    className={`px-4 py-2 text-right font-medium ${
                      txn.amount < 0
                        ? 'text-green-600'
                        : 'text-red-600'
                    }`}
                  >
                    {txn.amount < 0 ? '+' : '-'}${Math.abs(txn.amount).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountDetailPage;
